import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { PAGES } from 'constants/pages';
import { useGetTicketCommentsQuery, useGetTicketQuery } from 'data/__generated__';

import { TicketActionsSection } from './sections/TicketActionsSection';
import { TicketCommentsSection } from './sections/TicketCommentsSection';
import {
    TicketDatesSection,
    TicketGeneralSection,
    TicketLocationSection,
    TicketTrackingSection,
} from './sections/TicketInfoSections';

export const TicketDetails = () => {
    const { ticketId = '' } = useParams<{ ticketId: string }>();
    const { data, loading, error } = useGetTicketQuery({ variables: { ticketId } });
    const {
        data: commentsData,
        loading: commentsLoading,
        error: commentsError,
        refetch: refetchComments,
    } = useGetTicketCommentsQuery({ variables: { ticketId } });

    if (loading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (error || !data) {
        throw new Error('Une erreur est survenue lors de la récupération du ticket');
    }

    const { ticket } = data;

    return (
        <Container data-test="ticket-details-page">
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.ticketDetails} />
                </HeaderTitle>
                <Link to={PAGES.tickets.url}>
                    <TotemPrimaryButton $isSecondaryStyle>Retour</TotemPrimaryButton>
                </Link>
            </Header>
            <Content>
                <ScrollableContent>
                    <TicketGeneralSection ticket={ticket} />
                    <TicketLocationSection ticket={ticket} />
                    <TicketTrackingSection ticket={ticket} />
                    <TicketDatesSection ticket={ticket} />
                    <TicketActionsSection ticket={ticket} />
                    <TicketCommentsSection
                        comments={commentsData?.ticketComments}
                        loading={commentsLoading}
                        error={!!commentsError}
                        onRetry={() => {
                            void refetchComments();
                        }}
                    />
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    height: 100%;
    overflow-y: auto;
    padding: 15px;
    display: flex;
    flex-direction: column;
    gap: 15px;
`;
